import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useParams } from 'react-router-dom';
import {
  Button,
  Input,
  Modal,
  Select,
  StepsIndicator,
  Title
} from '../../../../components';
import { AppContext } from '../../../../context/AppContext';
import { emptyConstancyService } from '../../../../emptyObjects';
import { useNavigate, useResource } from '../../../../hooks';
import {
  Client,
  Collaborator,
  ConstancyService
} from '../../../../interfaces';

import './styles.scss';

export const Step1 = () => {
  const {
    createResource,
    fetchResource
  } = useResource<ConstancyService>();
  const {
    fetchResources: fetchClients
  } = useResource<Client>();
  const {
    fetchResources: fetchCollaborators
  } = useResource<Collaborator>();

  const navigate = useNavigate();
  const params = useParams();

  const {
    apiType,
    setOpenModal,
    soundEffects
  } = useContext(AppContext);

  const { t } = useTranslation();

  const [constancyService, setConstancyService] = useState<ConstancyService>(emptyConstancyService);
  const [constancyFound, setConstancyFound] = useState<boolean>(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [
    errors,
    setErrors
  ] = useState<{ [name: string]: string }>({});

  const validData = (): boolean => {
    if (constancyService.client.id === 'none') {
      setErrors({
        client: t('services.errors.client')
      });

      return false;
    }

    if (constancyService.collaborator.id === 'none') {
      setErrors({
        collaborator: t('services.errors.collaborator')
      });

      return false;
    }

    return true;
  };

  const getCollaborators = (clientID: string) => {
    fetchCollaborators(
      {
        resourcePath: '/admin/collaborators',
        filters: { s_clientID: clientID }
      },
      (data) => setCollaborators(data.items),
      (error: string) => Modal.fireError(error, setOpenModal, soundEffects)
    );
  };

  const getClients = (): Promise<Client[]> => {
    return new Promise((resolve, reject) => {
      fetchClients(
        {
          resourcePath: '/admin/clients',
          ordering: {
            s_orderBy: 'businessName',
            s_orderDirection: 'ASC'
          }
        },
        (data) => resolve(data.items),
        (error: string) => reject(error)
      );
    });
  };

  const handleChangeClient = (value: string) => {
    setConstancyService({
      ...constancyService,
      client: {
        id: value,
        businessName: constancyService.client.businessName,
        shortName: constancyService.client.shortName
      },
      collaborator: {
        id: 'none',
        name: value !== 'none' ? constancyService.collaborator.name : ''
      }
    });

    if (value !== 'none' && apiType === 'admin') {
      getCollaborators(value);
    } else {
      setCollaborators([]);
      setConstancyService(emptyConstancyService);
    }
  };

  const handleChangeCollaborator = (value: string) => {
    setConstancyService({
      ...constancyService,
      collaborator: {
        id: value,
        name: collaborators.find(element => element.id === value)?.name || ''
      }
    });
  };

  const getConstancyService = (): Promise<ConstancyService> => {
    return new Promise((resolve, reject) => {
      fetchResource(
        `/${apiType}/constancy-services/${params.constancyServiceID}`,
        (data: ConstancyService) => {
          resolve(data);
          setConstancyService(data);
        },
        (error: string) => reject(error)
      );
    });
  };

  const createConstancyService = (): Promise<ConstancyService> => {
    return new Promise((resolve, reject) => {
      createResource(
        `/${apiType}/constancy-services`,
        {
          clientID: constancyService.client.id,
          collaboratorID: constancyService.collaborator.id
        },
        (data: ConstancyService) => resolve(data),
        (error: string) => reject(error)
      );
    });
  };

  const handleContinue = async () => {
    if (validData()) {
      try {
        if (params.constancyServiceID === 'none') {
          if (constancyService.id === '') {
            const service = await createConstancyService();

            setConstancyService(service);

            navigate(`/constancy-request/step_2/${service.id}/${service.requestNumber}/${constancyService.client.id}/${constancyService.collaborator.id}`);
          }
        } else {
          navigate(`/constancy-request/step_2/${constancyService.id}/${constancyService.requestNumber}/${constancyService.client.id}/${constancyService.collaborator.id}`);
        }
      } catch (error) {
        Modal.fireError(error as string, setOpenModal, soundEffects);
      }
    }
  };

  const disableContinueButton = (!constancyFound && params.constancyServiceID !== 'none') || clients.length === 0;

  const alertEmptyCollaborators = collaborators.length === 0
    && constancyService.client.id !== 'none'
    && params.constancyServiceID === 'none';

  const getData = async () => {
    try {
      if (params.constancyServiceID !== 'none') {
        await getConstancyService();

        setConstancyFound(true);
      }

      if (apiType === 'admin') {
        setClients(await getClients());
      }
    } catch (error) {
      Modal.fireError(error as string, setOpenModal, soundEffects);
    }
  };

  useEffect(() => {
    getData();
  }, [apiType]);

  return (
    <>
      <div className='step-1-constancy__steps'>
        {
          apiType === 'admin' ? (
            <StepsIndicator
              stepsQuantity={3}
              currentStep={1}
            />
          ) : (
            <StepsIndicator
              stepsQuantity={4}
              currentStep={1}
            />
          )
        }
      </div>
      <div className='step-1-constancy__content'>
        <div className='step-1-constancy'>
          <Title title={t('services.constancyStep1')} />
          <div className='step-1-constancy__input-container'>
            <Title title={t('global.client')} type='secondary' />
            {
              params.constancyServiceID !== 'none' ? (
                <Input
                  type='text'
                  value={constancyService.client.businessName}
                  onChange={() => { }}
                  disabled={true}
                  id='client'
                />
              ) : (
                <Select
                  options={
                    clients.map((_client: Client) => {
                      return {
                        text: _client.businessName,
                        value: _client.id
                      };
                    })
                  }
                  id='client'
                  onChange={handleChangeClient}
                  value={constancyService.client.id}
                  disabled={clients.length === 0 || params.constancyServiceID !== 'none'}
                  hasError={clients.length === 0 && params.constancyServiceID === 'none' ? true : !!errors.client}
                  helperText={clients.length === 0 && params.constancyServiceID === 'none' ? t('global.clientsNotFound') || '' : errors.client || t('global.clientService') || ''}
                />
              )
            }
          </div>
          <div className='step-1-constancy__input-container'>
            <Title title={t('global.collaborator')} type='secondary' />
            {
              params.constancyServiceID !== 'none' ? (
                <Input
                  type='text'
                  value={constancyService.collaborator.name}
                  onChange={() => { }}
                  disabled={true}
                  id='collaborator'
                />
              ) : (
                <Select
                  id='collaborator'
                  options={
                    // eslint-disable-next-line max-len
                    collaborators.filter(objeto => objeto.isActive === true).map((_collaborator: Collaborator) => {
                      return {
                        text: _collaborator.name,
                        value: _collaborator.id
                      };
                    })
                  }
                  onChange={handleChangeCollaborator}
                  value={constancyService.collaborator.id}
                  disabled={collaborators.length === 0 || clients.length === 0}
                  hasError={alertEmptyCollaborators ? true : !!errors.collaborator}
                  helperText={alertEmptyCollaborators ? t('global.collaboratorsNotFound') || '' : errors.collaborator || t('global.collaboratorService') || ''}
                />
              )
            }
          </div>
          <div className='step-1-constancy__button-container'>
            <Button
              onClick={handleContinue}
              type='primary'
              label={t('global.continue') || ''}
              icon='rightArrow'
              iconPosition='right'
              disabled={disableContinueButton}
              id='save-button'
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Step1;
